import { createElement, forwardRef, type HTMLAttributes } from "react";
import clsx from "clsx";

const textVariants = {
  "body-sm": "text-body-sm",
  "body-md": "text-body-md",
  "body-lg": "text-body-lg",
  "label-sm": "text-label-sm",
  "label-md": "text-label-md",
  "label-lg": "text-label-lg",
  "heading-sm": "text-heading-sm",
  "heading-md": "text-heading-md",
  "heading-lg": "text-heading-lg",
  "heading-xl": "text-heading-xl",
} as const;

const textTones = {
  primary: "text-text-primary",
  secondary: "text-text-secondary",
  muted: "text-text-muted",
  foreground: "text-foreground",
  "muted-foreground": "text-muted-foreground",
} as const;

export type TextProps = HTMLAttributes<HTMLElement> & {
  as: "p" | "span";
  variant: keyof typeof textVariants;
  tone?: keyof typeof textTones;
};

/**
 * Typography only: no wrapper, default color, spacing, or data attributes.
 * Keep layout utilities at the call site. Tones preserve the distinct existing
 * color tokens; muted and muted-foreground are deliberately not interchangeable.
 */
export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { as: Component, variant, tone, className, ...props },
  ref,
) {
  // Preserve all supplied utilities, including typography overrides. Merging
  // Tailwind classes here could silently discard part of the original styling.
  return createElement(Component, {
    ...props,
    ref,
    className: clsx(textVariants[variant], tone && textTones[tone], className),
  });
});
